import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { EditPassportTitleFormVM } from './edit-passport-title-form.viewmodel';
import { EDIT_PASSPORT_TITLE_FORM_CONFIG, EditPassportTitleFieldKey } from './edit-passport-title-form.config';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { RichTextEditor } from '../../../../../global/rich-text-editor';
import { FormFieldError } from '../../../../../global/form/form-field-input/form-field-error';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import {
    APPLY_AND_CLOSE_TEXT,
    APPLY_TEXT, CANCEL_TEXT,
    EVENT_THUMBNAIL_IMAGE_TEXT,
    loadingText,
} from '../../../../../../constants/common-labels.constants';
import DefaultButton from '../../../../../global/default-button/default-button';
import {
    CONFIGURE_PASSPORT_PRIVATE_DESCRIPTION_INFO_TEXT,
    CONFIGURE_PASSPORT_PUBLIC_DESCRIPTION_INFO_TEXT,
    CONFIGURE_PASSPORT_THUMBNAIL_INFO_TEXT,
    CONFIGURE_PASSPORT_TITLE_INFO_TEXT,
} from '../passport-settings-form/passport-settings.constants';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import { UploadImageS3Presentation } from '../../../shared/upload-image/upload-image-to-s3.presentation';

interface IProps {
    vm: EditPassportTitleFormVM;
}

const EditPassportTitleFormBody = observer(({ vm }: IProps): JSX.Element => {
    const {
        eventTitleForPassport,
        eventPublicDescription,
        eventPrivateDescription,
    } = vm.getFormFields(EditPassportTitleFieldKey);

    return (
        <div className="passport-settings">
            <form>
                <FormField
                    label={vm.intl.formatMessage(
                        EDIT_PASSPORT_TITLE_FORM_CONFIG[EditPassportTitleFieldKey.TITLE].labelDescriptor
                    )}
                    className='with-editor send-connections-email-form__email-body'
                    infoIconText={vm.intl.formatMessage(
                        CONFIGURE_PASSPORT_TITLE_INFO_TEXT
                    )}
                >
                    <textarea
                        rows={2}
                        className='oe-form-field__control passport-form__textarea'
                        value={eventTitleForPassport.value as string}
                        onChange={({ target: { value } }) => eventTitleForPassport.setValue(value)}
                        onBlur={eventTitleForPassport.setTouched}
                    />
                    <FormFieldError
                        field={eventTitleForPassport}
                        labelDescriptor={EDIT_PASSPORT_TITLE_FORM_CONFIG[EditPassportTitleFieldKey.TITLE].labelDescriptor}
                    />
                </FormField>
                <FormField
                    label={vm.intl.formatMessage(
                        EDIT_PASSPORT_TITLE_FORM_CONFIG[EditPassportTitleFieldKey.PUBLIC_DESCRIPTION].labelDescriptor
                    )}
                    className='with-editor send-connections-email-form__email-body'
                    infoIconText={vm.intl.formatMessage(
                        CONFIGURE_PASSPORT_PUBLIC_DESCRIPTION_INFO_TEXT
                    )}
                >
                    <RichTextEditor
                        resizable
                        className='m-top-0px body-editor'
                        value={eventPublicDescription.value as string}
                        onChange={action((event, editor) => {
                            eventPublicDescription.setValue(editor.getData());
                        })}
                        onBlur={eventPublicDescription.setTouched}
                    />
                    <FormFieldError
                        field={eventPublicDescription}
                        labelDescriptor={EDIT_PASSPORT_TITLE_FORM_CONFIG[EditPassportTitleFieldKey.PUBLIC_DESCRIPTION].labelDescriptor}
                    />
                </FormField>
                <FormField
                    label={vm.intl.formatMessage(
                        EDIT_PASSPORT_TITLE_FORM_CONFIG[EditPassportTitleFieldKey.PRIVATE_DESCRIPTION].labelDescriptor
                    )}
                    className='with-editor send-connections-email-form__email-body'
                    infoIconText={vm.intl.formatMessage(
                        CONFIGURE_PASSPORT_PRIVATE_DESCRIPTION_INFO_TEXT
                    )}
                >
                    <RichTextEditor
                        resizable
                        className='m-top-0px body-editor'
                        value={eventPrivateDescription.value as string}
                        onChange={action((event, editor) => {
                            eventPrivateDescription.setValue(editor.getData());
                        })}
                        onBlur={eventPrivateDescription.setTouched}
                    />
                    <FormFieldError
                        field={eventPrivateDescription}
                        labelDescriptor={EDIT_PASSPORT_TITLE_FORM_CONFIG[EditPassportTitleFieldKey.PRIVATE_DESCRIPTION].labelDescriptor}
                    />
                </FormField>
                <UploadImageS3Presentation
                    vm={vm.thumbnailImageVM}
                    dimensions={1000}
                    fieldName={vm.intl.formatMessage(EVENT_THUMBNAIL_IMAGE_TEXT)}
                    infoIconText={vm.intl.formatMessage(CONFIGURE_PASSPORT_THUMBNAIL_INFO_TEXT)}
                />
            </form>
        </div>
    );
});

const EditPassportTitleFormFooter = observer(({ intl, saving, uploading, submitForm }): JSX.Element => {

    return (
        <>
            {saving && (
                <span className='oe-popup-layout__footer-status-area'>
                    {intl.formatMessage({
                        id: 'oe.customFields.dataIsBeingSaved',
                        description: 'Preloader message text',
                        defaultMessage: 'Data is being saved, please wait{suffix}',
                    }, {
                            suffix: '...'
                    })}
                </span>
            )}
            <div className='buttons-block buttons-block--from-right'>
                <DefaultCloseSubWindowButton
                    text={intl.formatMessage(APPLY_AND_CLOSE_TEXT)}
                    modifier={['fat', 'confirm-safe']}
                    onClick={submitForm}
                    disabled={saving || uploading}
                />
                <DefaultButton
                    text={intl.formatMessage(APPLY_TEXT)}
                    modifier={['fat', 'confirm-safe']}
                    onClick={submitForm}
                    disabled={saving || uploading}
                />
                <DefaultCloseSubWindowButton
                    text={intl.formatMessage(CANCEL_TEXT)}
                />
            </div>
        </>
    );
});

const EditPassportTitleFormPresentation = observer(({ vm }: IProps): JSX.Element => {
    if (vm.loading) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form meeting-form'>
                    {loadingText()}
                </div>
            }
        />;
    }

    if (!vm.formVM) {
        return <CommonPopupBodyFooterLayout
            body={null}
        />;
    }

    return (
        <CommonPopupBodyFooterLayout
            className='oe-form'
            body={<EditPassportTitleFormBody vm={vm} />}
            footer={<EditPassportTitleFormFooter
                saving={vm.saving}
                uploading={vm.thumbnailImageVM?.isImageUploading}
                submitForm={vm.submitForm}
                intl={vm.intl}
            />}
        />
    );
});

export { EditPassportTitleFormPresentation, EditPassportTitleFormBody, EditPassportTitleFormFooter };